import { PetType } from 'constants/pet-type';
import {
  AllergySegmentationIds,
  BreedSegmentationIds,
  CatAgeSegmentationIds,
  CatAllergySegmentationIds,
  CatNutritionSegmentationIds,
  DogAgeSegmentationIds,
  NutritionSegmentationIds,
  ProteinTagValues,
} from 'constants/segmentation';
import { InputProductType, WetFoodTagValues } from 'interfaces/api-input/input-product-type';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useRootStore } from 'stores/use-root-store';
import { CatProfile, DogProfile } from 'types/pet-profile';
import {
  addArrayPropertyIfNotEmpty,
  addPropertyIfValid,
  parseAndValidateCommaSeparatedValues,
  validateSingleValue,
} from './utils';

const VALID_DOG_AGES = Object.values(DogAgeSegmentationIds) as readonly string[];
const VALID_DOG_BREEDS = Object.values(BreedSegmentationIds) as readonly string[];
const VALID_DOG_NEEDS = Object.values(NutritionSegmentationIds) as readonly string[];
const VALID_DOG_ALLERGIES = Object.values(AllergySegmentationIds) as readonly string[];

const VALID_CAT_AGES = Object.values(CatAgeSegmentationIds) as readonly string[];
const VALID_CAT_NEEDS = Object.values(CatNutritionSegmentationIds) as readonly string[];
const VALID_CAT_ALLERGIES = Object.values(CatAllergySegmentationIds) as readonly string[];
const VALID_CAT_TASTE_PREFERENCES = Object.values(ProteinTagValues) as readonly string[];
const VALID_CAT_WET_FOOD_TYPES = Object.values(WetFoodTagValues) as readonly string[];
const VALID_FOOD_TYPES = Object.values(InputProductType) as readonly string[];

type QueryParam = string | string[] | undefined;

interface DogQueryParams {
  age: QueryParam;
  breed: QueryParam;
  needs: QueryParam;
  allergies: QueryParam;
  foodtypes: QueryParam;
}

interface CatQueryParams {
  age: QueryParam;
  needs: QueryParam;
  allergies: QueryParam;
  taste: QueryParam;
  wetfood: QueryParam;
  foodtypes: QueryParam;
}

const buildDogProfile = ({
  age,
  breed,
  needs,
  allergies,
  foodtypes,
}: DogQueryParams): DogProfile => {
  let profile: DogProfile = {
    petType: PetType.Dog,
    id: crypto.randomUUID(),
    selectedAge: undefined,
    selectedNeeds: undefined,
    selectedAllergies: undefined,
    selectedFoodTypes: undefined,
    lastTouched: undefined,
    updatedAt: undefined,
    selectedWeight: undefined,
    name: undefined,
    activityLevel: undefined,
    kcalUpdatedAt: undefined,
    dailyKcal: undefined,
    hasSpecialNeeds: undefined,
    bodyCondition: undefined,
    dateOfBirth: undefined,
    neutered: undefined,
  };

  const validAge = validateSingleValue(age, VALID_DOG_AGES);
  profile = addPropertyIfValid(
    profile,
    'selectedAge',
    validAge as DogAgeSegmentationIds | undefined
  );

  const validBreed = validateSingleValue(breed, VALID_DOG_BREEDS);
  profile = addPropertyIfValid(
    profile,
    'selectedBreed',
    validBreed as BreedSegmentationIds | undefined
  );

  const validNeeds = parseAndValidateCommaSeparatedValues(needs, VALID_DOG_NEEDS);
  profile = addArrayPropertyIfNotEmpty(
    profile,
    'selectedNeeds',
    validNeeds as NutritionSegmentationIds[]
  );

  if (validNeeds.includes('intolerance' as NutritionSegmentationIds)) {
    const validAllergies = parseAndValidateCommaSeparatedValues(allergies, VALID_DOG_ALLERGIES);
    profile = addArrayPropertyIfNotEmpty(
      profile,
      'selectedAllergies',
      validAllergies as AllergySegmentationIds[]
    );
  }

  const validFoodTypes = parseAndValidateCommaSeparatedValues(foodtypes, VALID_FOOD_TYPES);
  const foodTypesToApply =
    validFoodTypes.length > 0
      ? (validFoodTypes as InputProductType[])
      : [
          InputProductType.WetFood,
          InputProductType.DryFood,
          InputProductType.Barf,
          InputProductType.Snacks,
        ];
  profile = addArrayPropertyIfNotEmpty(profile, 'selectedFoodTypes', foodTypesToApply);

  return profile;
};

const buildCatProfile = ({
  age,
  needs,
  allergies,
  taste,
  wetfood,
  foodtypes,
}: CatQueryParams): CatProfile => {
  let profile: CatProfile = {
    petType: PetType.Cat,
    id: crypto.randomUUID(),
    selectedAge: undefined,
    selectedNeeds: undefined,
    selectedAllergies: undefined,
    selectedFoodTypes: undefined,
    lastTouched: undefined,
    updatedAt: undefined,
    selectedWeight: undefined,
    name: undefined,
    selectedWetFoodTypes: [],
    selectedTastePreferences: [],
    selectedDislikedProteins: [],
    activityLevel: undefined,
    kcalUpdatedAt: undefined,
    dailyKcal: undefined,
    hasSpecialNeeds: undefined,
    bodyCondition: undefined,
    dateOfBirth: undefined,
    neutered: undefined,
  };

  const validAge = validateSingleValue(age, VALID_CAT_AGES);
  profile = addPropertyIfValid(
    profile,
    'selectedAge',
    validAge as CatAgeSegmentationIds | undefined
  );

  const validNeeds = parseAndValidateCommaSeparatedValues(needs, VALID_CAT_NEEDS);
  profile = addArrayPropertyIfNotEmpty(
    profile,
    'selectedNeeds',
    validNeeds as CatNutritionSegmentationIds[]
  );

  if (validNeeds.includes('intolerancecat' as CatNutritionSegmentationIds)) {
    const validAllergies = parseAndValidateCommaSeparatedValues(allergies, VALID_CAT_ALLERGIES);
    profile = addArrayPropertyIfNotEmpty(
      profile,
      'selectedAllergies',
      validAllergies as CatAllergySegmentationIds[]
    );
  }

  const validTaste = parseAndValidateCommaSeparatedValues(taste, VALID_CAT_TASTE_PREFERENCES);
  profile = addArrayPropertyIfNotEmpty(
    profile,
    'selectedTastePreferences',
    validTaste as ProteinTagValues[]
  );

  const validFoodTypes = parseAndValidateCommaSeparatedValues(foodtypes, VALID_FOOD_TYPES);
  profile = addArrayPropertyIfNotEmpty(
    profile,
    'selectedFoodTypes',
    validFoodTypes as InputProductType[]
  );

  // Only process wet food types when wet_food is in foodtypes
  if (validFoodTypes.includes('wet_food' as InputProductType)) {
    const validWetFood = parseAndValidateCommaSeparatedValues(wetfood, VALID_CAT_WET_FOOD_TYPES);
    profile = addArrayPropertyIfNotEmpty(
      profile,
      'selectedWetFoodTypes',
      validWetFood as WetFoodTagValues[]
    );
  }

  return profile;
};

// TODO: reduce copy-pasta. Could be structured to have a config of validation rules see pt-8934-url-parsing
/**
 * This hook is used to set the pet profile from the query params.
 *
 * IMPORTANT: Requires pp-edit=1 query parameter to activate processing.
 * Without pp-edit=1, all pet profile parameters (age, breed, needs, allergies) are ignored.
 *
 * This safety mechanism prevents accidental application of pet profile data.
 *
 * Use pet=cat to build a cat profile, otherwise defaults to dog.
 *
 * Example URLs:
 * - Dog: /any-page?pp-edit=1&age=welpe&breed=retriever&needs=digestion
 * - Cat: /any-page?pp-edit=1&pet=cat&age=kitten&needs=digestioncat&taste=chicken,salmon&wetfood=pate
 */
export const useSetPetFromQueryParams = (): void => {
  const { isReady, query } = useRouter();
  const { age, breed, needs, allergies, pet, taste, wetfood, foodtypes } = query;
  const ppEdit = query['pp-edit'];
  const savePetProfile = useRootStore((store) => store.savePetProfile);

  useEffect(() => {
    if (!isReady) return;
    if (ppEdit !== '1') return;

    const isCat = pet === 'cat';

    const newProfile = isCat
      ? buildCatProfile({ age, needs, allergies, taste, wetfood, foodtypes })
      : buildDogProfile({ age, breed, needs, allergies, foodtypes });

    savePetProfile(newProfile);
  }, [
    isReady,
    age,
    breed,
    needs,
    allergies,
    ppEdit,
    pet,
    taste,
    wetfood,
    foodtypes,
    savePetProfile,
  ]);
};
